import { useRef } from 'react'
import { RAIL_MAX, RAIL_MIN, RAIL_DEFAULT, clampRailWidth } from '../lib/rail-width.ts'

/** Arrow-key step. Coarse enough to be useful, fine enough to land precisely. */
const STEP = 16

/**
 * The draggable edge of the nav rail.
 *
 * A separator rather than a button: it has a value, and assistive technology
 * announces and adjusts it the same way as the pointer does. Keyboard support
 * is not a nicety here — a control you can only operate by dragging is one a
 * keyboard user cannot operate at all.
 *
 * `onChange` fires continuously through a drag so the layout tracks the
 * pointer; `onCommit` fires once at the end, which is where the value is
 * persisted rather than writing to storage on every pointer frame.
 */
export function SidebarResizer({
  width, onChange, onCommit,
}: {
  width: number
  onChange: (px: number) => void
  onCommit: (px: number) => void
}) {
  const dragging = useRef(false)

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Secondary buttons open context menus; they should not start a drag.
    if (e.button !== 0) return
    dragging.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    document.body.classList.add('is-resizing')
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return
    // The rail is flush against the viewport's left edge, so the pointer's x
    // position is the width being asked for.
    onChange(clampRailWidth(e.clientX))
  }

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return
    dragging.current = false
    e.currentTarget.releasePointerCapture(e.pointerId)
    document.body.classList.remove('is-resizing')
    onCommit(width)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const next =
      e.key === 'ArrowLeft' ? width - STEP
      : e.key === 'ArrowRight' ? width + STEP
      : e.key === 'Home' ? RAIL_MIN
      : e.key === 'End' ? RAIL_MAX
      : null
    if (next === null) return
    e.preventDefault() // arrows would otherwise scroll the page underneath
    const clamped = clampRailWidth(next)
    onChange(clamped)
    onCommit(clamped)
  }

  return (
    <div
      className="sidebar__resizer"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize navigation"
      aria-valuenow={width}
      aria-valuemin={RAIL_MIN}
      aria-valuemax={RAIL_MAX}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
      onDoubleClick={() => {
        onChange(RAIL_DEFAULT)
        onCommit(RAIL_DEFAULT)
      }}
    />
  )
}
